import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
} from '@mui/material';
import type { Location, Action } from 'history';

const MESSAGE = 'You have unsaved changes. Leave without saving?';

type GuardFormState = {
	isDirty: boolean;
	isSubmitting: boolean;
	isSubmitSuccessful: boolean;
};

type PendingNavigation = { location: Location; action: Action };

/**
 * Warns before losing unsaved work. In-app navigation is intercepted via
 * history.block and confirmed with the same MUI Dialog layout as the
 * app's EdConfirmModal, so it looks identical to every other confirm in
 * the app; browser refresh/close still uses beforeunload, which browsers
 * do not allow to be styled.
 *
 * Pass react-hook-form's formState. The guard is active only while the
 * form is dirty, not submitting, and not just successfully submitted —
 * isSubmitSuccessful matters because post-save navigation via
 * history.goBack() is asynchronous (popstate lands after isSubmitting
 * flips back to false while the form is still dirty). reset() (e.g.
 * "Save and add another") re-arms the guard.
 */
export const UnsavedChangesGuard: React.FC<{ formState: GuardFormState }> = ({
	formState,
}) => {
	const { isDirty, isSubmitting, isSubmitSuccessful } = formState;
	const when = isDirty && !isSubmitting && !isSubmitSuccessful;

	const history = useHistory();
	const [pending, setPending] = useState<PendingNavigation | null>(null);
	const whenRef = useRef(when);
	whenRef.current = when;
	const unblockRef = useRef<(() => void) | null>(null);

	useEffect(() => {
		if (!when) return;
		const handler = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = MESSAGE;
		};
		window.addEventListener('beforeunload', handler);
		return () => window.removeEventListener('beforeunload', handler);
	}, [when]);

	useEffect(() => {
		const unblock = history.block(((location: Location, action: Action) => {
			if (!whenRef.current) return undefined;
			setPending({ location, action });
			return false;
		}) as any);
		unblockRef.current = unblock;
		return () => {
			unblock();
			unblockRef.current = null;
		};
	}, [history]);

	const stay = () => setPending(null);

	const leave = () => {
		const target = pending;
		setPending(null);
		unblockRef.current?.();
		unblockRef.current = null;
		if (!target) return;
		if (target.action === 'REPLACE') {
			history.replace(target.location);
		} else {
			// PUSH and POP both navigate to the blocked target explicitly.
			// history v4 cannot distinguish back from forward (both are
			// POP), so goBack() would send a forward-navigation backward;
			// pushing the intended location is direction-proof at the cost
			// of one extra history entry.
			history.push(target.location);
		}
	};

	return (
		<Dialog
			open={Boolean(pending)}
			onClose={stay}
			aria-labelledby="unsaved-changes-title"
			aria-describedby="unsaved-changes-description"
		>
			<DialogContent>
				<DialogContentText id="unsaved-changes-description">
					{MESSAGE}
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button color="warning" onClick={stay}>
					No
				</Button>
				<Button sx={{ color: '#6C63FF' }} onClick={leave} autoFocus>
					Yes
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default UnsavedChangesGuard;
